import { Brick, BrickContext, registerBrick, Transaction, Query, ErrorFlow } from 'olympe';

import Papa from 'papaparse';
import Evaluation from '../../../models/Evaluation';
import InspectionEntity from '../../../models/InspectionEntity';
import TranslatedString from '../../../models/TranslatedString';
import { CSV_HEADERS } from './constants';

export default class ImportTranslationCSV extends Brick {
    /**
     * @override
     * @protected
     * @param {!BrickContext} $
     * @param {File} file
     * @param {function()} forwardEvent
     * @param {function(number)} setUpdatedTerms
     * @param {function(*)} setErrorFlow
     */
    async update($, [file], [forwardEvent, setUpdatedTerms, setErrorFlow]) {
        // Read file content
        file.getContentAsString().then((content) => {
            // Parse CSV
            Papa.parse(content, {
                header: true,
                skipEmptyLines: true,
                complete: async (results) => {
                    // Get data
                    const data = results.data;
                    // Init transaction
                    const transaction = new Transaction(true);
                    let updatedTerms = 0;
                    // Read all data line
                    for (let i = 0; i < data.length; ++i) {
                        let update = false;
                        const line = data[i];
                        // Ensure TAG property is defined in line (might be missing on last / empty lines)
                        if (line.hasOwnProperty(CSV_HEADERS.TAG)) {
                            // Get Tag and values
                            const tag = line[CSV_HEADERS.TAG].replace(/'/g, '');
                            // --  en value
                            const enValueNew = line[CSV_HEADERS.EN_VALUE];
                            // --  fr value
                            const frValueNew = line[CSV_HEADERS.FR_VALUE];
                            // --  de value
                            const deValueNew = line[CSV_HEADERS.DE_VALUE];
                            // --  it value
                            const itValueNew = line[CSV_HEADERS.IT_VALUE];
                            // --  zh value
                            const zhValueNew = line[CSV_HEADERS.ZH_VALUE];
                            // --  es value
                            const esValueNew = line[CSV_HEADERS.ES_VALUE];
                            // --  pt value
                            const ptValueNew = line[CSV_HEADERS.PT_VALUE];
                            // --  vi value
                            const viValueNew = line[CSV_HEADERS.VI_VALUE];
                            // --  tr value
                            const trValueNew = line[CSV_HEADERS.TR_VALUE];
                            // --  description value
                            const descriptionNew = line[CSV_HEADERS.DESCRIPTION];
                            // --  item value
                            const itemValue = line[CSV_HEADERS.ITEM];

                            if (itemValue.toLowerCase().includes('description')) {
                                const isEvaluationEntity = itemValue === 'Result Description';
                                const descriptionStringFromCache = Query.fromTag(tag, isEvaluationEntity ? Evaluation : InspectionEntity)
                                    .executeFromCache()
                                    .getFirst();

                                const descriptionValueActual = descriptionStringFromCache?.getDescription() ?? '';
                                if (this.replaceString(descriptionNew) !== this.replaceString(descriptionValueActual)) {
                                    transaction.update(tag, isEvaluationEntity ? Evaluation.descriptionProp : InspectionEntity.descriptionProp, descriptionNew);
                                    update = true;
                                }
                            } else {
                                // Get Actual values
                                const translatedStringFromCache = Query.fromTag(tag, TranslatedString).executeFromCache().getFirst();

                                const enValueActual = translatedStringFromCache?.get(TranslatedString.enProp) ?? '';
                                const frValueActual = translatedStringFromCache?.get(TranslatedString.frProp) ?? '';
                                const deValueActual = translatedStringFromCache?.get(TranslatedString.deProp) ?? '';
                                const itValueActual = translatedStringFromCache?.get(TranslatedString.itProp) ?? '';
                                const zhValueActual = translatedStringFromCache?.get(TranslatedString.zhProp) ?? '';
                                const esValueActual = translatedStringFromCache?.get(TranslatedString.esProp) ?? '';
                                const ptValueActual = translatedStringFromCache?.get(TranslatedString.ptProp) ?? '';
                                const viValueActual = translatedStringFromCache?.get(TranslatedString.viProp) ?? '';
                                const trValueActual = translatedStringFromCache?.get(TranslatedString.trProp) ?? '';

                                // Create transaction update (only if different)
                                // -- EN Value
                                if (this.replaceString(enValueNew) !== this.replaceString(enValueActual)) {
                                    const nameEntity = (
                                        await Query.fromTag(tag, TranslatedString).follow(InspectionEntity.translatedNameRel.getInverse()).andReturn().execute()
                                    ).getFirst();

                                    transaction.update(tag, TranslatedString.enProp, enValueNew);

                                    if (nameEntity) {
                                        transaction.update(nameEntity, InspectionEntity.nameProp, enValueNew);
                                    }

                                    update = true;
                                }

                                // -- FR Value
                                if (this.replaceString(frValueNew) !== this.replaceString(frValueActual)) {
                                    transaction.update(tag, TranslatedString.frProp, frValueNew);
                                    update = true;
                                }

                                // -- DE Value
                                if (this.replaceString(deValueNew) !== this.replaceString(deValueActual)) {
                                    transaction.update(tag, TranslatedString.deProp, deValueNew);
                                    update = true;
                                }

                                // -- IT Value
                                if (this.replaceString(itValueNew) !== this.replaceString(itValueActual)) {
                                    transaction.update(tag, TranslatedString.itProp, itValueNew);
                                    update = true;
                                }

                                // -- ZH Value
                                if (this.replaceString(zhValueNew) !== this.replaceString(zhValueActual)) {
                                    transaction.update(tag, TranslatedString.zhProp, zhValueNew);
                                    update = true;
                                }

                                // -- ES Value
                                if (this.replaceString(esValueNew) !== this.replaceString(esValueActual)) {
                                    transaction.update(tag, TranslatedString.esProp, esValueNew);
                                    update = true;
                                }

                                // -- PT Value
                                if (this.replaceString(ptValueNew) !== this.replaceString(ptValueActual)) {
                                    transaction.update(tag, TranslatedString.ptProp, ptValueNew);
                                    update = true;
                                }

                                // -- VI Value
                                if (this.replaceString(viValueNew) !== this.replaceString(viValueActual)) {
                                    transaction.update(tag, TranslatedString.viProp, viValueNew);
                                    update = true;
                                }

                                // -- TR Value
                                if (this.replaceString(trValueNew) !== this.replaceString(trValueActual)) {
                                    transaction.update(tag, TranslatedString.trProp, trValueNew);
                                    update = true;
                                }
                            }
                            // If an update occurred, increase updated terms
                            if (update) {
                                ++updatedTerms;
                            }
                        }
                    }
                    //  ------- EXECUTE TRANSACTION -------
                    transaction
                        .executeAsLarge()
                        .then(() => {
                            setUpdatedTerms(updatedTerms);
                            forwardEvent();
                        })
                        .catch((error) => {
                            setErrorFlow(ErrorFlow.create(`Failed to update translations. - ${error}`, 3));
                        });
                },
            });
        });
    }

    replaceString(string) {
        return string.replace(/\r?\n|\r|\n/g, '');
    }
}

registerBrick('018a8dae577906d9ce21', ImportTranslationCSV);
